<script>
/**
 * @component PatientPharmacyDuty
 * @description UC-REG-296 — "Información comunitaria": farmacias de guardia
 *              del día para el vecino (Campana). Muestra el aviso fijo de
 *              verificación telefónica, la fecha, y las farmacias de turno
 *              (24 h) y abiertas con su horario, dirección y teléfono. Permite
 *              moverse un día hacia atrás o adelante.
 * @example <PatientPharmacyDuty />
 */
import pharmacyDutyService from "@/services/pharmacy-duty";
import {
  PHARMACY_DUTY_NOTICE,
  addDutyDays,
  entryStatusLabel,
  formatDutyDate,
  telHref,
  todayDutyDate,
} from "@/services/pharmacy-duty-view";

export default {
  name: "PatientPharmacyDuty",

  data() {
    return {
      notice: PHARMACY_DUTY_NOTICE,
      /** @type {string} día consultado `AAAA-MM-DD` */
      date: todayDutyDate(),
      /** @type {Object|null} respuesta de GET /pharmacy-duty/today */
      duty: null,
      isLoading: true,
    };
  },

  computed: {
    /** @returns {string} */
    dateLabel() {
      return formatDutyDate(this.date);
    },

    /** @returns {Array<Object>} */
    entries() {
      return (this.duty && this.duty.entries) || [];
    },

    /** @returns {Array<Object>} farmacias de turno (24 h) */
    onDutyEntries() {
      return this.entries.filter((e) => e.onDuty);
    },

    /** @returns {Array<Object>} farmacias abiertas con horario */
    openEntries() {
      return this.entries.filter((e) => !e.onDuty);
    },

    /** @returns {boolean} */
    isPublished() {
      return Boolean(this.duty && this.duty.published);
    },

    /** @returns {boolean} */
    isToday() {
      return this.date === todayDutyDate();
    },
  },

  created() {
    this.load();
  },

  methods: {
    entryStatusLabel,
    telHref,

    /** @description Carga la nómina del día seleccionado. */
    async load() {
      this.isLoading = true;
      try {
        this.duty = await pharmacyDutyService.getToday(this.date);
      } catch (err) {
        this.duty = null;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Cambia de día y recarga.
     * @param {number} days - -1 ayer, +1 mañana
     */
    shift(days) {
      this.date = addDutyDays(this.date, days);
      this.load();
    },

    /** @description Vuelve a hoy. */
    goToday() {
      this.date = todayDutyDate();
      this.load();
    },

    /**
     * @description Dirección completa de la farmacia.
     * @param {Object} pharmacy
     * @returns {string}
     */
    addressOf(pharmacy) {
      return [pharmacy.address, pharmacy.city].filter(Boolean).join(", ");
    },
  },
};
</script>

<template lang="pug">
.pd
  .pd__notice
    micon.pd__notice-icon(name="info")
    span {{ notice }}

  .pd__header
    button.pd__nav(type="button" title="Día anterior" @click="shift(-1)")
      micon(name="chevron_left")
    .pd__date
      span.pd__date-label {{ dateLabel }}
      button.pd__today(v-if="!isToday" type="button" @click="goToday") Volver a hoy
    button.pd__nav(type="button" title="Día siguiente" @click="shift(1)")
      micon(name="chevron_right")

  div(v-loading="isLoading")
    p.pd__notes(v-if="isPublished && duty.notes") {{ duty.notes }}

    .pd__empty(v-if="!isLoading && !isPublished")
      micon.pd__empty-icon(name="local_pharmacy")
      p.pd__empty-text Todavía no se publicó la nómina de farmacias para este día.

    .pd__empty(v-else-if="!isLoading && isPublished && !entries.length")
      micon.pd__empty-icon(name="local_pharmacy")
      p.pd__empty-text No hay farmacias de guardia informadas para este día.

    template(v-if="onDutyEntries.length")
      h2.pd__section-title Farmacias de turno
      .pd__card.pd__card--duty(v-for="e in onDutyEntries" :key="'d' + e.pharmacy._id")
        .pd__card-main
          span.pd__card-name {{ e.pharmacy.commercialName }}
          span.pd__card-status {{ entryStatusLabel(e) }}
          span.pd__card-address(v-if="addressOf(e.pharmacy)")
            micon(name="location_on")
            | {{ addressOf(e.pharmacy) }}
        a.pd__card-phone(v-if="telHref(e.pharmacy.phone)" :href="telHref(e.pharmacy.phone)")
          micon(name="call")
          span {{ e.pharmacy.phone }}

    template(v-if="openEntries.length")
      h2.pd__section-title Farmacias abiertas
      .pd__card(v-for="e in openEntries" :key="'o' + e.pharmacy._id")
        .pd__card-main
          span.pd__card-name {{ e.pharmacy.commercialName }}
          span.pd__card-status.pd__card-status--open {{ entryStatusLabel(e) }}
          span.pd__card-address(v-if="addressOf(e.pharmacy)")
            micon(name="location_on")
            | {{ addressOf(e.pharmacy) }}
        a.pd__card-phone(v-if="telHref(e.pharmacy.phone)" :href="telHref(e.pharmacy.phone)")
          micon(name="call")
          span {{ e.pharmacy.phone }}
</template>

<style lang="scss" scoped>
.pd {
  max-width: 800px;
  margin: 0 auto;

  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #fff7e6;
    border: 1px solid #f5d58f;
    color: #7a4d00;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  &__notice-icon {
    width: 18px;
    height: 18px;
    fill: #c77800;
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 14px;
  }

  &__nav {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    .micon {
      width: 18px;
      height: 18px;
      fill: $navy;
    }

    &:hover {
      border-color: var(--org-color-primary, $primary);
    }
  }

  &__date {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 0;
    text-align: center;
  }

  &__date-label {
    font-size: 16px;
    font-weight: 700;
    color: $navy;
  }

  &__today {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: var(--org-color-primary, $primary);
  }

  &__notes {
    font-size: 13px;
    color: #4b5563;
    margin: 0 0 12px;
  }

  &__section-title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6b7280;
    margin: 16px 0 8px;
  }

  &__card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    margin-bottom: 8px;

    &--duty {
      border-left: 4px solid var(--org-color-primary, $primary);
    }
  }

  &__card-main {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  &__card-name {
    font-size: 15px;
    font-weight: 700;
    color: $navy;
  }

  &__card-status {
    display: inline-flex;
    align-self: flex-start;
    padding: 2px 8px;
    border-radius: 6px;
    background: var(--org-color-primary, $primary);
    color: #fff;
    font-size: 11px;
    font-weight: 700;

    &--open {
      background: #e5e7eb;
      color: #374151;
    }
  }

  &__card-address {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #6b7280;

    .micon {
      width: 14px;
      height: 14px;
      fill: #9ca3af;
    }
  }

  &__card-phone {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    text-decoration: none;
    white-space: nowrap;

    .micon {
      width: 16px;
      height: 16px;
      fill: $navy;
    }

    &:hover {
      border-color: var(--org-color-primary, $primary);
      color: var(--org-color-primary, $primary);
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36px 20px;
    border: 2px dashed #d1d5db;
    border-radius: 12px;
    background: #fff;
  }

  &__empty-icon {
    width: 44px;
    height: 44px;
    fill: #9ca3af;
    margin-bottom: 10px;
  }

  &__empty-text {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    text-align: center;
  }
}

@media (max-width: 767px) {
  .pd {
    &__card {
      flex-wrap: wrap;
    }

    &__card-phone {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
